import { CATEGORY_PUBLICITY, DIFFICULTY, ENTITY_ID, QUESTION_CORRECT_ANSWER } from '../../domain/domainCodes.js'
import { ApiError, api } from '../../lib/api'
import { dataParamsForm, readDataTable } from '../../lib/dataTable'
import { download } from '../../lib/download'
import { fileToBase64 } from '../../lib/file'
import { toFormData } from '../../lib/form'
import { upload } from '../../lib/upload'
import { categoryBannerUrl, questionImageUrl } from '../../lib/media'
import { isEnglish } from '../../lib/i18n'
import { topicNames } from './topicNames.mjs'

/**
 * Training content — `Features/Admin/CategoriesManagement/Endpoint.cs`.
 *
 *   getContentPage    -> GET  /api/admin/categories-management/
 *   listCategories    -> POST /api/admin/categories-management/list    form + DataTables
 *   listChapters      -> POST /api/admin/categories-management/videos
 *   saveCategory      -> POST /api/admin/categories-management/save    multipart, HTML reply
 *   deleteCategory    -> POST /api/admin/categories-management/delete
 *   deleteChapter     -> POST /api/admin/categories-management/delete-video
 *   exportCategory    -> POST /api/admin/categories-management/export
 *   exportQuestions   -> GET  /api/admin/categories-management/export-questions
 *   importCategory    -> POST /api/admin/categories-management/upload  multipart
 *
 * Nothing here reads `src/data/content.js` any more: topics, questions and certificates are
 * all served by the API.
 *
 * TWO THINGS TO KNOW BEFORE READING `saveCategory`.
 *
 * 1. IT ANSWERS HTML, NOT JSON. The reply is a page carrying
 *    `window.parent.finishSavingCategory(ok, 'message', 'id', 'name', publicity)` — the
 *    iframe convention, because the form posts video files. `upload.js` parses it, and THE
 *    ID IT RETURNS IS THE POINT: a new topic has none until it has been saved once.
 *
 * 2. FILES ARE MATCHED BY FILENAME, not by field name. The handler looks for a part whose
 *    FileName is exactly `topicBanner`, and per chapter `{chapterId}-ar.mp4` and
 *    `{chapterId}-en.mp4`. A part appended under the right field with the wrong filename is
 *    accepted and silently ignored.
 */

const BASE = '/api/admin/categories-management'

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/** The server clamps a page to 100, and topics are a bounded set well under it. */
const MAX_CATEGORIES = 100

/** The difficulty levels the question step offers. */
export async function getContentPage(signal) {
  const dto = (await api.get(`${BASE}/`, { signal })) ?? {}

  return {
    difficulties: (dto.DifficultyLevels ?? []).map((level) => ({
      value: level.DifficultyId,
      label: label(level.ArabicName, level.EnglishName),
    })),
  }
}

/**
 * @param {number|null} publicity  the visibility filter; `null` is every topic.
 */
export async function listCategories({
  publicity = null,
  page = 1,
  pageSize = 10,
  search = '',
  sort,
  signal,
} = {}) {
  /* `publicity` rides alongside the DataTables keys in the same form, and is parsed with
     `TryParse` — so omitting it is what "no filter" means. */
  const extra = {}
  if (publicity !== null && publicity !== '') extra.publicity = String(publicity)

  const form = dataParamsForm(
    { page, pageSize, search, sort, columns: ['name', 'nameEn', 'createdOn', 'chapters'] },
    extra,
  )

  const response = await api.post(`${BASE}/list`, form, { signal })
  const { rows, total, totalUnfiltered } = readDataTable(response)

  return { rows: rows.map(toCategoryRow), total, totalUnfiltered }
}

function toCategoryRow(dto) {
  return {
    id: dto.Id,
    ...topicNames(dto, isEnglish),
    description: label(dto.ArabicDescription, dto.EnglishDescription),
    descriptionEn: dto.EnglishDescription ?? '',
    createdOn: dto.CreationDate ?? '',
    /* THE NAMES THE TABLE READS.

       `DataTable` takes a cell as `row[column.key]` unless the column defines `render`, and
       these four columns define neither `render` nor `value` — so the keys have to BE the
       property names. They were not: the columns ask for `arabicLength`, `englishLength`,
       `chapters` and `questions`, and this returned `length`, `chaptersCount` and
       `questionsCount`. Every one of those cells came out blank, on every topic, whether it
       was imported or built here.

       The two lengths are also kept apart rather than collapsed with `label()`. There is a
       column per language — the clock beside the Arabic name and the clock beside the
       English one — and picking one by UI locale left both showing the same number. The
       server formats each as MM:SS. */
    arabicLength: dto.ArabicLength ?? '',
    englishLength: dto.EnglishLength ?? '',
    chapters: dto.ChaptersCount ?? 0,
    questions: dto.QuestionsCount ?? 0,
    position: dto.Position ?? 0,
    publicity: dto.Publicity ?? CATEGORY_PUBLICITY.PUBLIC,
    /* The topic's storage folder. A chapter row carries its own path map but not its
       topic's, and a video is filed under both — so the player needs this to name a file. */
    pathMap: dto.PathMap ?? '',
    // Composed in one place; this path was guessed wrong here once. See `lib/media.js`.
    banner: categoryBannerUrl(dto.BannerName),
  }
}

/**
 * One topic with its chapters.
 *
 * There is no get-one endpoint and no search by id, so the row is taken from a page large
 * enough to hold the whole set. Topics number in the tens.
 */
export async function getCategory(id, signal) {
  const [list, chapters] = await Promise.all([
    listCategories({ pageSize: MAX_CATEGORIES, signal }),
    listChapters(id, signal),
  ])

  const category = list.rows.find((row) => String(row.id) === String(id))
  if (!category) return null

  return { ...category, chapters }
}

/** The chapters (videos) of one topic. */
export async function listChapters(categoryId, signal) {
  const rows =
    (await api.post(`${BASE}/videos`, toFormData({ categoryId: String(categoryId) }), { signal })) ?? []

  return rows.map((dto) => ({
    id: dto.Id,
    titleAr: dto.ArabicName ?? '',
    titleEn: dto.EnglishName ?? '',
    /* Seconds; the two `Duration*String` fields are the same values already written out
       for display, so neither side has to format. */
    lengthAr: dto.ArabicLength ?? 0,
    lengthEn: dto.EnglishLength ?? 0,
    durationAr: dto.DurationArString ?? '',
    durationEn: dto.DurationEnString ?? '',
    createdOn: dto.CreationDate ?? '',
    categoryId: dto.ParentCategoryId,
    file: dto.ArFile ?? '',
  }))
}

/**
 * The whole wizard in one post: the topic, its chapters, the chapters it dropped, its
 * questions and their order.
 *
 * @param {object} input
 * @param {File}     [input.banner]   appended with the FileName `topicBanner`
 * @param {Array}    [input.chapters] each may carry `fileAr` / `fileEn`
 * @param {number[]} [input.deletedChapters]
 * @param {Array}    [input.questions]
 * @returns {{ok: boolean, message: string, id: number|null}}
 */
export async function saveCategory(input) {
  const request = {
    Data: {
      /* `-1` CREATES; anything else UPDATES. `SaveCategoryHandler` treats -1 and only -1 as
         the create branch, so an unsaved topic sent as `0` took the update branch,
         `FindAsync(0)` found nothing, no row was written — and it still answered
         `Result: true` with "تم تحديث الموضوع بنجاح". The create branch also guards the
         banner requirement and builds the topic's content directories, so none of that ran
         either. */
      Id: input.id ? Number(input.id) : ENTITY_ID.NEW,
      ArabicName: input.name ?? '',
      ArabicDescription: input.description ?? '',
      EnglishName: input.nameEn ?? '',
      EnglishDescription: input.descriptionEn ?? '',
      // A string server-side, not a number.
      Position: String(input.position ?? ''),
      Publicity: Number(input.publicity ?? CATEGORY_PUBLICITY.PUBLIC),
    },
    UpdatedVideos: (input.chapters ?? []).map((chapter) => ({
      Id: Number(chapter.id ?? 0),
      ArabicName: chapter.titleAr ?? '',
      EnglishName: chapter.titleEn ?? '',
      ArabicLength: Number(chapter.lengthAr ?? 0),
      EnglishLength: Number(chapter.lengthEn ?? 0),
    })),
    DeletedVideos: (input.deletedChapters ?? []).map(Number),
    UpdatedQuestions: await Promise.all((input.questions ?? []).map(toQuestionData)),
    ChaptersOrders: (input.chapters ?? []).map((chapter, index) => ({
      Id: Number(chapter.id ?? 0),
      Order: index + 1,
    })),
  }

  const form = new FormData()
  form.append('requestJson', JSON.stringify(request))

  // The FILENAME is the key here, not the field name. See the note at the top.
  /* ONLY A REAL FILE. Editing a topic loads its existing banner back into the form, and the
     server hands that back as a URL STRING, not a File — appending it throws
     "parameter 2 is not of type 'Blob'" while the request is still being built, so the save
     never left the browser and the screen reported nothing at all. An unchanged banner is
     simply not re-sent; the server keeps the one it already has. */
  if (input.banner instanceof Blob) form.append('files', input.banner, 'topicBanner')

  for (const chapter of input.chapters ?? []) {
    if (chapter.fileAr) form.append('files', chapter.fileAr, `${chapter.id}-ar.mp4`)
    if (chapter.fileEn) form.append('files', chapter.fileEn, `${chapter.id}-en.mp4`)
  }

  try {
    const data = await upload(`${BASE}/save`, form)
    return { ok: true, message: data?.Message ?? '', id: data?.id ?? input.id ?? null }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, id: null }
    throw error
  }
}

/**
 * The legacy field names, kept because the command binds them literally.
 *
 * READS THE SHAPE THE APP ACTUALLY HAS. This mapped `question.textAr`, `question.optionsAr`,
 * `correctAnswer`, `difficultyId` and `categoryId` — and NOTHING in this product has ever
 * produced those names. `QuestionFormDrawer`, `getQuestion` and `listQuestions` all speak
 * `text` / `textEn` / `option1…option4` / `option1En…option4En` / `answer` / `difficulty` /
 * `category`, so every field here read `undefined` and went up empty. The server checks the
 * Arabic question text first, which is why adding a question to a topic always came back
 * "يجب إدخال نص السؤال باللغتين العربية والإنجليزية" no matter how completely it was filled in.
 */
async function toQuestionData(question) {
  return {
    /* Only a saved question has an id worth sending; anything else is `null`, which the
       handler reads as "add this one". A negative placeholder id must never go over. */
    QuestionId: question.id > 0 ? question.id : null,
    DrpDnLstAnswers: Number(question.answer ?? QUESTION_CORRECT_ANSWER.OPTION_ONE),
    DrpDnLstDifficultyLevel: Number(question.difficulty ?? DIFFICULTY.HARD),
    /* Sent for the contract's sake only: both handler branches set the question's category
       to the TOPIC being saved and never read this. */
    DrpDnLstCategories: Number(question.category ?? 0),
    TxtArabicQ: question.text ?? '',
    TxtEnglishQ: question.textEn ?? '',
    TxtArabicOption1: question.option1 ?? '',
    TxtArabicOption2: question.option2 ?? '',
    TxtArabicOption3: question.option3 ?? '',
    TxtArabicOption4: question.option4 ?? '',
    TxtEnglishOption1: question.option1En ?? '',
    TxtEnglishOption2: question.option2En ?? '',
    TxtEnglishOption3: question.option3En ?? '',
    TxtEnglishOption4: question.option4En ?? '',
    /* Base64 inside the JSON, not a file part — that is how the handler reads them. */
    FULDArabicQuestionImage: await questionImageData(question.imageAr),
    FULDEnglishQuestionImage: await questionImageData(question.imageEn),
  }
}

/**
 * A question image as the JSON wants it: base64, or null.
 *
 * The picker hands back a `File`, and a `File` inside `JSON.stringify` serialises to `{}` —
 * which is not the `string?` the command declares, so the whole request failed to
 * deserialise and the topic came back as a bare "an error occurred". An image loaded back
 * for editing is a URL instead, and re-sending that would store the URL as the picture.
 */
async function questionImageData(image) {
  if (image instanceof Blob) return (await fileToBase64(image)) || null
  if (typeof image === 'string' && image.startsWith('data:')) return image
  return null
}

async function act(path, body) {
  try {
    const data = await api.post(`${BASE}/${path}`, body)
    return {
      // The slice answers `Result` on some actions and `success` on others.
      ok: data?.Result !== false && data?.success !== false,
      message: data?.Message ?? data?.message ?? '',
    }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export const deleteCategory = (categoryId) =>
  act('delete', toFormData({ CategoryId: String(categoryId) }))

export const deleteChapter = (chapterId) =>
  act('delete-video', toFormData({ chapterId: String(chapterId) }))

/**
 * The topic as a file, for moving it to another installation.
 *
 * A POST that answers EITHER a file or `{ Result: false, Message }`, so a refusal arrives
 * as JSON where a download was expected — `download.js` reports it rather than saving a
 * file whose contents are an error message.
 */
export async function exportCategory(categoryId) {
  try {
    const saved = await download(`${BASE}/export`, {
      method: 'POST',
      body: toFormData({ categoryId: String(categoryId) }),
    })
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** The topic's question bank as a spreadsheet. */
export async function exportCategoryQuestions(categoryId) {
  try {
    const saved = await download(`${BASE}/export-questions?categoryId=${Number(categoryId)}`)
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** The other half of the export: a topic file from another installation. */
export async function importCategory({ file }) {
  if (!file) return { ok: false, message: '' }

  const form = new FormData()
  // The handler reads `form.Files["fULDCategory"]`, by name.
  form.append('fULDCategory', file)

  try {
    const data = await upload(`${BASE}/upload`, form)
    return { ok: data?.success !== false, message: data?.message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/* ------------------------------------------------------------------ *
 * Still mock — `EditQuestions` and `CertificateSettings` own these and
 * are later slices in this phase.
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * Questions - `Features/Admin/EditQuestions/Endpoint.cs`, `QuestionManagers`
 *
 *   getQuestionsPage -> GET  /api/admin/edit-questions/
 *   listQuestions    -> POST /api/admin/edit-questions/list        form: categoryId
 *   getQuestion      -> POST /api/admin/edit-questions/get         form: questionId
 *   saveQuestion     -> POST /api/admin/edit-questions/save        multipart
 *   deleteQuestion   -> POST /api/admin/edit-questions/delete      form: questionId
 *   importQuestions  -> POST /api/admin/edit-questions/bulk-import multipart: ExcelFile
 *
 * A THIRD FILE CONVENTION IN ONE PHASE. Question images are real file PARTS, read by field
 * name (`form.Files["ArabicImageFile"]`). Categories matches parts by FILENAME; programmes
 * take base64 inside the JSON. Nothing carries between them.
 * ------------------------------------------------------------------ */

const QUESTIONS = '/api/admin/edit-questions'

/** The topics and difficulty levels the form offers, and the counts per each. */
export async function getQuestionsPage(categoryId, signal) {
  const query = categoryId ? `?categoryId=${Number(categoryId)}` : ''
  const dto = (await api.get(`${QUESTIONS}/${query}`, { signal })) ?? {}

  return {
    categoryId: dto.CategoryId ?? null,
    categories: (dto.Categories ?? []).map((category) => ({
      value: category.Id ?? category.CategoryId,
      label: label(category.ArabicName, category.EnglishName),
    })),
    difficulties: (dto.DifficultyLevels ?? []).map((level) => ({
      value: level.DifficultyId ?? level.Id,
      label: label(level.ArabicName, level.EnglishName),
    })),
    /* How many questions each topic and each difficulty holds - the counts the screen
       shows above its list. */
    byDifficulty: (dto.DifficultyLevelStats ?? []).map((stat) => ({
      id: stat.DifficultyId ?? stat.Id,
      name: label(stat.ArabicName, stat.EnglishName),
      count: stat.Count ?? 0,
    })),
    byCategory: (dto.CategoryStats ?? []).map((stat) => ({
      id: stat.CategoryId ?? stat.Id,
      name: label(stat.ArabicName, stat.EnglishName),
      count: stat.Count ?? 0,
    })),
  }
}

/**
 * The question bank, optionally narrowed to one topic, and further to one difficulty.
 *
 * `QuestionItemDto` is ALREADY LOCALIZED - `Question`, `Option1`..`Option4`, `Level` and
 * `Category` are one language, chosen server-side. The edit form needs both, which is what
 * `getQuestion` is for.
 */
export async function listQuestions({ categoryId = null, difficultyId = null, signal } = {}) {
  const fields = {}
  if (categoryId) fields.categoryId = String(categoryId)
  if (difficultyId) fields.difficultyId = String(difficultyId)

  const rows = (await api.post(`${QUESTIONS}/list`, toFormData(fields), { signal })) ?? []

  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      text: dto.Question ?? '',
      options: [dto.Option1, dto.Option2, dto.Option3, dto.Option4].map((o) => o ?? ''),
      /* 1-based, which is what the correct-answer column shows. */
      answer: dto.Answer ?? QUESTION_CORRECT_ANSWER.NONE,
      difficulty: dto.Level ?? '',
      category: dto.Category ?? '',
    })),
    total: rows.length,
  }
}

/** One question, both languages, for the edit form. */
export async function getQuestion(questionId, signal) {
  let dto
  try {
    dto = await api.post(`${QUESTIONS}/get`, toFormData({ questionId: String(questionId) }), { signal })
  } catch (error) {
    /* An id that matches no question answers 200 `{ Result: false, Message: "Question not
       found" }`. The endpoint drops the handler's QUESTION_NOT_FOUND code and keeps only
       the words, so they are what is matched: null means "no such question", and the
       page says so instead of reporting a failed load. Any other refusal still throws. */
    if (error instanceof ApiError && error.code === 'RESULT' && /not found/i.test(error.message)) return null
    throw error
  }

  if (!dto) return null

  return {
    id: dto.QuestionId,
    text: dto.Text ?? '',
    textEn: dto.EngText ?? '',
    option1: dto.Option1 ?? '',
    option2: dto.Option2 ?? '',
    option3: dto.Option3 ?? '',
    option4: dto.Option4 ?? '',
    option1En: dto.EngOption1 ?? '',
    option2En: dto.EngOption2 ?? '',
    option3En: dto.EngOption3 ?? '',
    option4En: dto.EngOption4 ?? '',
    answer: dto.AnswerNumber ?? QUESTION_CORRECT_ANSWER.OPTION_ONE,
    category: dto.CategoryID ?? null,
    difficulty: dto.DifficultyID ?? null,
    imageAr: questionImageUrl(dto.ArabicImageFileName),
    imageEn: questionImageUrl(dto.EnglishImageFileName),
  }
}

/**
 * Create or update. `QuestionId` above zero means update; the handler reads anything else
 * as new.
 *
 * @param {File} [values.imageArFile] posted as the PART `ArabicImageFile`.
 */
export async function saveQuestion(values) {
  const form = new FormData()
  form.append('QuestionId', String(values.id ?? ENTITY_ID.NONE))
  form.append('Answer', String(values.answer ?? QUESTION_CORRECT_ANSWER.OPTION_ONE))
  form.append('DifficultyLevelId', String(values.difficulty ?? ENTITY_ID.NONE))
  form.append('CategoryId', String(values.category ?? ENTITY_ID.NONE))
  form.append('ArabicQuestion', values.text ?? '')
  form.append('EnglishQuestion', values.textEn ?? '')

  for (const index of [1, 2, 3, 4]) {
    form.append(`ArabicOption${index}`, values[`option${index}`] ?? '')
    form.append(`EnglishOption${index}`, values[`option${index}En`] ?? '')
  }

  // Read by FIELD name here, unlike the categories wizard next door.
  /* `imageAr` / `imageEn` are what the DRAWER stores (`onChange={set('imageAr')}`); the
     names read here were `imageArFile` / `imageEnFile`, which nothing ever sets — so both
     pickers accepted a file, showed "تم الرفع", and posted nothing.
     `instanceof Blob` because the same fields hold a URL STRING when an existing question is
     opened for editing, and appending that would throw. */
  if (values.imageAr instanceof Blob) form.append('ArabicImageFile', values.imageAr)
  if (values.imageEn instanceof Blob) form.append('EnglishImageFile', values.imageEn)

  try {
    const data = await upload(`${QUESTIONS}/save`, form)
    return { ok: data?.Result !== false, message: data?.Message ?? '', id: data?.QuestionId ?? values.id }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export async function deleteQuestion(questionId) {
  try {
    const data = await api.post(`${QUESTIONS}/delete`, toFormData({ questionId: String(questionId) }))
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** `root/CTemplate/{PathMap}` -> the served PNG, or null when no artwork was uploaded. */
const templateArtwork = (path) => {
  const trimmed = String(path ?? '').trim()
  if (!trimmed || trimmed.endsWith('/')) return null
  return `/${trimmed.replace(/^\/+/, '')}.png`
}

/** A spreadsheet of questions. The part is named `ExcelFile`. */
export async function importQuestions({ file }) {
  if (!file) return { ok: false, message: '' }

  const form = new FormData()
  form.append('ExcelFile', file)

  try {
    const data = await upload(`${QUESTIONS}/bulk-import`, form)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/* ------------------------------------------------------------------ *
 * Certificates - `Features/Admin/CertificateSettings/Endpoint.cs`
 * `ContentManagers` OR `CertificateValiditionManagers` - the only guard in this phase
 * that admits either of two roles.
 *
 *   listCertificates    -> POST /CertificateSettings/GetAllCertificates
 *   getCertificate      -> POST /CertificateSettings/GetCertificate      { CertificateId }
 *   saveCertificate     -> POST /CertificateSettings/Save                multipart, HTML reply
 *   cloneCertificate    -> POST /CertificateSettings/CloneCertificate    { CertificateId }
 *   deleteCertificate   -> POST /CertificateSettings/DeleteCertificate   { CertificateId }
 *   getTemplateLayout   -> GET  /CertificateSettings/GetTemplateProperties
 *   saveTemplateLayout  -> POST /CertificateSettings/UpdateTemplateProperties
 * ------------------------------------------------------------------ */

const CERTIFICATES = '/CertificateSettings'

export async function listCertificates(signal) {
  const rows = (await api.post(`${CERTIFICATES}/GetAllCertificates`, undefined, { signal })) ?? []

  return {
    rows: (Array.isArray(rows) ? rows : (rows.CertificatesData ?? [])).map((dto) => ({
      id: dto.Id,
      name: dto.ArabicName ?? dto.Name ?? '',
      nameEn: dto.EnglishName ?? '',
      uploadedOn: dto.UploadDate ?? '',
      prefix: dto.IdentifierPrefix ?? '',
      /* The artwork each language is printed on.
         `ArPath` / `EnPath` are ALREADY a full relative path — the handler builds
         `root/{CTemplate}/{PathMap}` — so composing the directory onto them again gave
         `/root/CTemplate/root%2FCTemplate%2F…`, which 404s and leaves the editor with a
         blank sheet. The extension is not stored either: the legacy appends `.png` when it
         loads the image (`fabric.Image.fromURL(baseUrl + image + '.png')`), and so does
         this. */
      arabicTemplate: templateArtwork(dto.ArPath),
      englishTemplate: templateArtwork(dto.EnPath),
    })),
    total: rows.length,
  }
}

export async function getCertificate(certificateId, signal) {
  const dto = await api.post(
    `${CERTIFICATES}/GetCertificate`,
    { CertificateId: Number(certificateId) },
    { signal },
  )

  if (!dto) return null

  return {
    id: dto.Id,
    name: dto.ArabicName ?? '',
    nameEn: dto.EnglishName ?? '',
    prefix: dto.IdentifierPrefix ?? '',
    uploadedOn: dto.UploadDate ?? '',
  }
}

/**
 * Create or update. Answers the `finishUpload` HTML callback rather than JSON, because it
 * carries the two artwork files - `upload.js` reads it.
 */
export async function saveCertificate(values) {
  const form = new FormData()
  if (values.id) form.append('certificateId', String(values.id))
  form.append('ArabicName', values.name ?? '')
  form.append('EnglishName', values.nameEn ?? '')
  form.append('PrefixText', values.prefix ?? '')

  // Bound as `IFormFile` by parameter name, so the field names are these exactly.
  if (values.fileAr) form.append('ArabicImage', values.fileAr)
  if (values.fileEn) form.append('EnglishImage', values.fileEn)

  try {
    const data = await upload(`${CERTIFICATES}/Save`, form)
    return { ok: true, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

async function certificateAction(path, certificateId) {
  try {
    const data = await api.post(`${CERTIFICATES}/${path}`, { CertificateId: Number(certificateId) })
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export const cloneCertificate = (certificateId) =>
  certificateAction('CloneCertificate', certificateId)

export const deleteCertificate = (certificateId) =>
  certificateAction('DeleteCertificate', certificateId)

/**
 * Where the four printed lines sit on the artwork, for one language.
 *
 * EVERY VALUE IS A STRING server-side - positions, sizes and the three booleans alike
 * (`"false"`, not `false`). The editor works in numbers and booleans, so this is the only
 * place that translation happens.
 *
 * @param {'arabic'|'english'} templateType
 */
export async function getTemplateLayout(certificateId, templateType, signal) {
  const query = new URLSearchParams({ certificateId: String(certificateId), templateType })
  const body = await api.get(`${CERTIFICATES}/GetTemplateProperties?${query}`, { signal })
  /* `data`, LOWER CASE — this endpoint answers `Results.Json(new { data = … })` while the
     rest of the slice is PascalCase. Reading only `Data` found nothing, so every field below
     fell to its zero and the editor collapsed all four layers into a zero-size box at the
     origin: an empty sheet, on a certificate that has a layout saved. */
  const dto = body?.Data ?? body?.data ?? body

  if (!dto) return null

  return {
    name: toLayer(dto, 'Name'),
    program: toLayer(dto, 'Program'),
    date: toLayer(dto, 'Date'),
    code: toLayer(dto, 'CertId'),
  }
}

const asBool = (value) => String(value ?? '').toLowerCase() === 'true'
const asNumber = (value) => Number(value ?? 0) || 0

/* THE CANVAS THE STORED NUMBERS ARE MEASURED AGAINST — `var canvasW = 1528, canvasH = 1080`
   in the legacy's js.cshtml, which is why its artwork spec is exact to the pixel. It saves
   `Math.round(layer.left)` and `.top`, absolute pixels in that space; this editor works in
   percentages so a layout survives any zoom. Read one way, written back the other: without
   this an X of 490px was applied as 490%, far off the sheet. */
const SHEET = { width: 1528, height: 1080 }

const pxToPercent = (value, axis) => Math.round((asNumber(value) / SHEET[axis]) * 1000) / 10
const percentToPx = (value, axis) => Math.round(((Number(value) || 0) / 100) * SHEET[axis])

/** One block of nine fields, named by its prefix. */
function toLayer(dto, prefix) {
  return {
    font: dto[`${prefix}Font`] ?? 'Arial',
    color: dto[`${prefix}FontColor`] ?? '#000000',
    size: asNumber(dto[`${prefix}Size`]),
    x: pxToPercent(dto[`${prefix}PositionX`], 'width'),
    y: pxToPercent(dto[`${prefix}PositionY`], 'height'),
    bold: asBool(dto[`${prefix}Bold`]),
    italic: asBool(dto[`${prefix}Italic`]),
    centered: asBool(dto[`${prefix}AutoCenter`]),
    offset: asNumber(dto[`${prefix}CenterOffsetH`]),
  }
}

export async function saveTemplateLayout({ certificateId, templateType, layout }) {
  const body = {
    CertificateId: Number(certificateId),
    TemplateType: templateType,
    ...fromLayer(layout.name, 'Name'),
    ...fromLayer(layout.program, 'Program'),
    ...fromLayer(layout.date, 'Date'),
    ...fromLayer(layout.code, 'CertId'),
  }

  try {
    const data = await api.post(`${CERTIFICATES}/UpdateTemplateProperties`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/** Back to strings, which is what the DTO declares. */
function fromLayer(layer, prefix) {
  const value = layer ?? {}
  return {
    [`${prefix}Font`]: String(value.font ?? 'Arial'),
    [`${prefix}FontColor`]: String(value.color ?? '#000000'),
    [`${prefix}Size`]: String(value.size ?? 0),
    [`${prefix}PositionX`]: String(percentToPx(value.x, 'width')),
    [`${prefix}PositionY`]: String(percentToPx(value.y, 'height')),
    [`${prefix}Bold`]: value.bold ? 'true' : 'false',
    [`${prefix}Italic`]: value.italic ? 'true' : 'false',
    [`${prefix}AutoCenter`]: value.centered ? 'true' : 'false',
    [`${prefix}CenterOffsetH`]: String(value.offset ?? 0),
  }
}

/** GET EditQuestions?categoryId= — the topic's own bank. */
/** The topic's own bank. */
export async function listCategoryQuestions(categoryId, signal) {
  return (await listQuestions({ categoryId, signal })).rows
}

/**
 * The option lists the question form offers.
 *
 * The categories half is real now; the difficulty levels come from the page payload, and
 * fall back to the mock only while that call is in flight.
 */
export async function listLookups(signal) {
  const [{ rows }, page] = await Promise.all([
    listCategories({ pageSize: MAX_CATEGORIES, signal }),
    getContentPage(signal),
  ])

  return {
    categories: rows.map((row) => ({ value: row.id, label: row.name })),
    difficulties: page.difficulties,
  }
}
